import { HitlToolRegistrySchema, type HitlToolRegistry } from './types';
import { ASK_USER_CHOICE_TOOL_NAME, PRESENT_PLAN_TOOL_NAME } from './types';

// HITL tool registries are split by use-case so each agent invocation only
// advertises tools that are actually relevant for that flow:
//
// - jobStartHitlToolRegistry — used by the report-generation kickoff
//   (POST /jobs). The planner subagent calls present_plan; nothing in
//   this flow asks free-form questions.
// - chatHitlToolRegistry — used by the in-chat orchestrator invoke. The
//   orchestrator may need to ask the user clarifying questions, but the
//   planner is not invoked from this path.
//
// Both schemas MUST be OpenAI strict-mode compliant (additionalProperties:
// false at every object level, every declared property listed in required,
// optional fields modeled as nullable type unions). Strict tool calling on
// the orchestrator is the lowest common denominator across the models we
// bind — Anthropic and Gemini accept strict-compliant schemas as well, so
// strict compliance here is a forward-compatible contract for any future
// model swap. Compliance is asserted by tests/hitlRegistry.strict.test.ts.

const PRESENT_PLAN_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    plan: {
      type: 'string',
      description: 'The full execution plan (briefing) in markdown format.',
    },
    scenario: {
      type: 'string',
      enum: ['generate-report', 'rewrite-narrative', 'entity-updates', 'error-fixes'],
      description: 'The classified scenario type.',
    },
    entityTypes: {
      type: 'array',
      items: { type: 'string' },
      description: 'List of entity types to extract.',
    },
    summary: {
      type: ['string', 'null'],
      description: 'Brief one-line summary of what the plan will do. Null when omitted.',
    },
    technical_plan: {
      type: ['string', 'null'],
      description:
        'Detailed extraction plan for the orchestrator (structured markdown). Null when omitted.',
    },
    entities: {
      type: ['array', 'null'],
      description:
        'Key facts the planner intends to extract, with confidence ratings. Null when none.',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          category: { type: 'string' },
          label: { type: 'string' },
          details: { type: ['string', 'null'] },
          confidence: { type: 'string', enum: ['high', 'medium', 'low'] },
          source: { type: 'string' },
        },
        required: ['category', 'label', 'details', 'confidence', 'source'],
      },
    },
    gaps: {
      type: ['array', 'null'],
      description:
        'Uncertain or ambiguous findings the officer should confirm. Null when none.',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          category: { type: 'string' },
          description: { type: 'string' },
          type: {
            type: 'string',
            enum: ['ambiguous', 'missing', 'multiple-options'],
          },
          options: {
            type: ['array', 'null'],
            items: { type: 'string' },
          },
        },
        required: ['category', 'description', 'type', 'options'],
      },
    },
  },
  required: [
    'plan',
    'scenario',
    'entityTypes',
    'summary',
    'technical_plan',
    'entities',
    'gaps',
  ],
};

const ASK_USER_CHOICE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    question: {
      type: 'string',
      description: 'The question text shown to the user.',
    },
    options: {
      type: 'array',
      description: 'The list of selectable options.',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          label: { type: 'string' },
          value: { type: 'string' },
          description: { type: ['string', 'null'] },
        },
        required: ['label', 'value', 'description'],
      },
    },
    title: {
      type: ['string', 'null'],
      description: 'Optional card title shown above the question.',
    },
    allowOther: {
      type: ['boolean', 'null'],
      description: 'Whether free-form "Other" input is allowed.',
    },
    otherLabel: {
      type: ['string', 'null'],
      description: 'Optional label for the free-form "Other" input.',
    },
    allowSkip: {
      type: ['boolean', 'null'],
      description: 'Whether the user can skip answering this question.',
    },
    submitLabel: {
      type: ['string', 'null'],
      description: 'Optional label for the submit button.',
    },
    multiSelect: {
      type: ['boolean', 'null'],
      description: 'Whether multiple choices can be selected.',
    },
    minSelections: {
      type: ['number', 'null'],
      description: 'Minimum number of options that must be selected.',
    },
    maxSelections: {
      type: ['number', 'null'],
      description: 'Maximum number of options that can be selected.',
    },
  },
  required: [
    'question',
    'options',
    'title',
    'allowOther',
    'otherLabel',
    'allowSkip',
    'submitLabel',
    'multiSelect',
    'minSelections',
    'maxSelections',
  ],
};

export const jobStartHitlToolRegistry: HitlToolRegistry = HitlToolRegistrySchema.parse({
  [PRESENT_PLAN_TOOL_NAME]: {
    name: PRESENT_PLAN_TOOL_NAME,
    description:
      'Present the execution plan to the user for review and approval before proceeding with report generation.',
    schema: PRESENT_PLAN_SCHEMA,
    agentNames: ['planner'],
  },
});

export const chatHitlToolRegistry: HitlToolRegistry = HitlToolRegistrySchema.parse({
  [ASK_USER_CHOICE_TOOL_NAME]: {
    name: ASK_USER_CHOICE_TOOL_NAME,
    description:
      'Ask the user to select one or more choices and optionally provide an "Other" answer.',
    schema: ASK_USER_CHOICE_SCHEMA,
    agentNames: ['main_orchestrator'],
  },
});
